import { Link } from 'react-router-dom';
import { ContactLink } from '@/components/contact';

/**
 * Terms of Service Page
 *
 * Legal terms governing the use of FlowIO services.
 * This is a public page accessible without authentication.
 *
 * WP-CONTACT-1: Updated to use ContactLink instead of mailto
 */
export default function TermsOfServicePage() {
  const lastUpdated = 'February 11, 2026';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="flex items-center">
            <svg
              className="h-8 w-8"
              viewBox="0 0 100 100"
              aria-label="FlowIO Logo"
            >
              <defs>
                <linearGradient id="tos-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#0ea5e9' }} />
                  <stop offset="100%" style={{ stopColor: '#8b5cf6' }} />
                </linearGradient>
              </defs>
              <path
                d="M20 30 Q50 30 50 50 T80 70"
                stroke="url(#tos-gradient)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                opacity="0.8"
              />
              <path
                d="M20 50 Q50 50 80 50"
                stroke="url(#tos-gradient)"
                strokeWidth="12"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M20 70 Q50 70 50 50 T80 30"
                stroke="url(#tos-gradient)"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                opacity="0.6"
              />
              <circle cx="20" cy="50" r="8" fill="#0ea5e9" />
              <circle cx="80" cy="50" r="8" fill="#8b5cf6" />
            </svg>
            <span className="ml-2 text-xl font-bold text-gray-900">FlowIO</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12">
          {/* Title */}
          <header className="mb-10 pb-8 border-b border-gray-200">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-600">
              Last updated: {lastUpdated}
            </p>
          </header>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to FlowIO. These Terms of Service ("Terms") govern your access to and use of
              the FlowIO website, applications, and services (collectively, the "Service"). Please
              read these Terms carefully before using our Service.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using the Service, you agree to be bound by these Terms. If you
              disagree with any part of these Terms, you may not access the Service.
            </p>
          </section>

          {/* Section 1: Service Description */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. Service Description
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              FlowIO is a personal finance application available on web and mobile platforms
              that provides:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Automated aggregation of financial data from your bank accounts, credit cards, and other financial institutions</li>
              <li>Visual representation of your money flow through Sankey-style diagrams</li>
              <li>Transaction categorization and analysis tools, including automated categorization using machine learning</li>
              <li>Household financial management with multi-user support</li>
              <li>Budgeting and financial planning features</li>
              <li>Document upload and processing for bank statements and financial documents</li>
              <li>Data import from other personal finance applications (Mint, YNAB, Quicken, and others)</li>
              <li>Mobile applications for iOS and Android with biometric authentication support</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              The Service is designed for personal financial management purposes and does not
              provide financial, investment, tax, or legal advice. Automated transaction
              categorization is provided for convenience; you are responsible for verifying
              the accuracy of any categorization.
            </p>
          </section>

          {/* Section 2: Eligibility */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Eligibility
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To use the Service, you must:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into binding agreements</li>
              <li>Provide accurate and complete registration information</li>
              <li>Not be prohibited from using the Service under applicable laws</li>
            </ul>
          </section>

          {/* Section 3: Account Terms */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. Account Terms
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              3.1 Account Creation
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You must create an account to access the Service. When creating an account, you
              agree to provide accurate, current, and complete information. You are responsible
              for maintaining the confidentiality of your account credentials.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              3.2 Household Accounts
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              FlowIO supports household accounts where multiple users can share access to
              financial data. The primary account holder is responsible for managing household
              membership and is liable for all activity within the household account.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              3.3 Account Security
            </h3>
            <p className="text-gray-700 leading-relaxed">
              You are responsible for all activities that occur under your account. You must
              immediately notify us of any unauthorized use of your account or any other
              security breach. We recommend enabling multi-factor authentication (MFA) for
              enhanced account security.
            </p>
          </section>

          {/* Section 4: User Responsibilities */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. User Responsibilities
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When using the Service, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use the Service only for lawful purposes and in accordance with these Terms</li>
              <li>Provide accurate credentials when connecting your financial accounts</li>
              <li>Keep your account information and connected account credentials secure</li>
              <li>Promptly update any information that changes</li>
              <li>Not attempt to gain unauthorized access to any part of the Service</li>
              <li>Not interfere with or disrupt the Service or servers</li>
              <li>Not use the Service to transmit any malicious code or conduct illegal activities</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          {/* Section 5: Data Usage and Privacy */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Data Usage and Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your privacy is important to us. Our collection, use, and sharing of your
              personal and financial information is governed by our{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              By using the Service, you consent to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The collection and processing of your financial data as described in our Privacy Policy</li>
              <li>The secure storage of your account credentials and financial information</li>
              <li>The use of aggregated, anonymized data for service improvement and analytics</li>
              <li>The automated processing of your transactions using machine learning for categorization</li>
              <li>The processing and storage of documents you upload (bank statements, CSV files, etc.)</li>
              <li>The import and processing of data from other financial services you choose to connect</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We implement industry-standard security measures to protect your data, including
              encryption at rest and in transit, but cannot guarantee absolute security.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3 mt-6">
              5.1 Uploaded Content
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you upload documents (such as bank statements or financial exports), you
              represent and warrant that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You have the right to upload and share the document with us</li>
              <li>The document belongs to you or your household</li>
              <li>You are not uploading documents belonging to others without authorization</li>
              <li>The document does not contain malicious code or content</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We process uploaded documents using automated systems to extract financial data.
              Original documents are encrypted and automatically deleted within 30 days of
              successful processing.
            </p>
          </section>

          {/* Section 6: Third-Party Services */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              6. Third-Party Services
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              6.1 Financial Data Aggregation
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              FlowIO uses third-party services to connect to your financial institutions and
              retrieve your financial data:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
              <li>
                <strong>Plaid Inc.:</strong> By connecting your accounts through Plaid, you
                authorize Plaid to access your financial information on your behalf. Your use
                of Plaid is subject to{' '}
                <a
                  href="https://plaid.com/legal/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Plaid's End User Privacy Policy
                </a>.
              </li>
              <li>
                <strong>Akoya:</strong> Depending on your financial institution, we may use
                Akoya as an alternative aggregation service. Akoya provides direct API
                connections to participating banks. Your use is subject to{' '}
                <a
                  href="https://www.akoya.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Akoya's Privacy Policy
                </a>.
              </li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              6.2 Payment Processing (Stripe)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              FlowIO uses Stripe, Inc. for subscription billing and payment processing. Your
              payment information is handled directly by Stripe and is subject to Stripe's{' '}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Privacy Policy
              </a>
              . We do not store your complete credit card information on our servers.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              6.3 Third-Party Disclaimer
            </h3>
            <p className="text-gray-700 leading-relaxed">
              We are not responsible for the practices, content, or policies of third-party
              services. Your interactions with third-party services are governed by their
              respective terms and privacy policies.
            </p>
          </section>

          {/* Section 7: Subscription and Billing */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              7. Subscription and Billing
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              7.1 Subscription Plans
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              FlowIO offers various subscription plans with different features and pricing.
              Current pricing and plan details are available on our website. We reserve the
              right to modify pricing with 30 days' notice to existing subscribers.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              7.2 Billing Cycle
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Subscriptions are billed in advance on a recurring basis (monthly or annually,
              depending on your plan). Your subscription will automatically renew unless
              cancelled before the renewal date.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              7.3 Cancellation and Refunds
            </h3>
            <p className="text-gray-700 leading-relaxed">
              You may cancel your subscription at any time through your account settings.
              Cancellation takes effect at the end of the current billing period. Refunds
              may be issued at our discretion and in accordance with applicable law.
            </p>
          </section>

          {/* Section 8: Intellectual Property */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              8. Intellectual Property
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Service and its original content, features, and functionality are owned by
              FlowIO and are protected by international copyright, trademark, patent, trade
              secret, and other intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You retain ownership of your financial data. By using the Service, you grant us
              a limited license to use, process, and display your data solely for the purpose
              of providing the Service to you.
            </p>
          </section>

          {/* Section 9: Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              9. Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4 uppercase text-sm font-medium">
              To the maximum extent permitted by applicable law:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
              <li>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied</li>
              <li>We do not warrant that the Service will be uninterrupted, secure, or error-free</li>
              <li>We are not responsible for the accuracy or completeness of data retrieved from your financial institutions</li>
              <li>We are not liable for any decisions you make based on information provided by the Service</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              In no event shall FlowIO, its directors, employees, partners, agents, suppliers,
              or affiliates be liable for any indirect, incidental, special, consequential, or
              punitive damages, including without limitation, loss of profits, data, use,
              goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Your access to or use of or inability to access or use the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
          </section>

          {/* Section 10: Indemnification */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              10. Indemnification
            </h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to defend, indemnify, and hold harmless FlowIO and its licensors,
              service providers, employees, agents, officers, and directors from and against
              any claims, damages, obligations, losses, liabilities, costs, or expenses arising
              from: (a) your use of the Service; (b) your violation of these Terms; (c) your
              violation of any third-party rights; or (d) any content you submit to the Service.
            </p>
          </section>

          {/* Section 11: Termination */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              11. Termination
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may terminate or suspend your account and access to the Service immediately,
              without prior notice or liability, for any reason, including without limitation
              if you breach these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Upon termination:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Your right to use the Service will immediately cease</li>
              <li>We may delete your account and all associated data</li>
              <li>You may request export of your data before account deletion, subject to our data retention policies</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              All provisions of these Terms which by their nature should survive termination
              shall survive, including ownership provisions, warranty disclaimers, indemnity,
              and limitations of liability.
            </p>
          </section>

          {/* Section 12: Changes to Terms */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              12. Changes to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify or replace these Terms at any time at our sole
              discretion. If a revision is material, we will provide at least 30 days' notice
              prior to any new terms taking effect.
            </p>
            <p className="text-gray-700 leading-relaxed">
              What constitutes a material change will be determined at our sole discretion.
              By continuing to access or use our Service after any revisions become effective,
              you agree to be bound by the revised terms.
            </p>
          </section>

          {/* Section 13: Governing Law */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              13. Governing Law and Dispute Resolution
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              13.1 Governing Law
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of
              the State of Delaware, United States, without regard to its conflict of law
              provisions.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              13.2 Binding Arbitration
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS,
              INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</strong>
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              You and FlowIO agree that any dispute, claim, or controversy arising out of or
              relating to these Terms or the Service (collectively, "Disputes") will be
              resolved through binding individual arbitration rather than in court, except
              that either party may seek equitable relief in court for infringement or misuse
              of intellectual property rights.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Arbitration Rules:</strong> Arbitration will be conducted by JAMS under
              its Streamlined Arbitration Rules and Procedures. The arbitration will be held
              in Delaware or, at your election, conducted via telephone or video conference.
              The arbitrator's decision will be final and binding.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>No Class Actions:</strong> You and FlowIO agree that each party may
              bring Disputes against the other only in an individual capacity and not as a
              plaintiff or class member in any purported class or representative proceeding.
              The arbitrator may not consolidate more than one person's claims and may not
              preside over any form of representative or class proceeding.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Opt-Out:</strong> You may opt out of this arbitration agreement by
              sending written notice to{' '}
              <ContactLink
                topic="legal"
                context="TermsOfServicePage - Arbitration opt-out"
                className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              >
                our legal team
              </ContactLink>{' '}
              within 30 days of creating your account. Your notice must include your name,
              email address, and a clear statement that you wish to opt out of this
              arbitration agreement.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              13.3 Exceptions to Arbitration
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Notwithstanding the above, either party may:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
              <li>Bring an individual action in small claims court for disputes within that court's jurisdiction</li>
              <li>Seek injunctive or other equitable relief in court to prevent infringement of intellectual property rights</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              13.4 Judicial Forum
            </h3>
            <p className="text-gray-700 leading-relaxed">
              If the arbitration agreement is found unenforceable, or for matters not subject
              to arbitration, you and FlowIO agree to submit to the exclusive jurisdiction of
              the state or federal courts located in Delaware. You consent to the personal
              jurisdiction of such courts and waive any objection based on venue or
              inconvenient forum.
            </p>
          </section>

          {/* Section 14: Miscellaneous */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              14. Miscellaneous
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Entire Agreement:</strong> These Terms constitute the entire agreement
              between you and FlowIO regarding the Service and supersede all prior agreements
              and understandings.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Severability:</strong> If any provision of these Terms is held to be
              invalid or unenforceable, the remaining provisions will remain in full force
              and effect.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Waiver:</strong> Our failure to enforce any right or provision of these
              Terms will not be considered a waiver of those rights.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Assignment:</strong> You may not assign or transfer these Terms or your
              rights under these Terms without our prior written consent. We may assign our
              rights and obligations under these Terms without restriction.
            </p>
          </section>

          {/* Section 15: Contact */}
          <section className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              15. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700 font-medium">FlowIO Support</p>
              <p className="text-gray-700">
                <ContactLink
                  topic="support"
                  context="TermsOfServicePage - Contact section"
                  className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
                >
                  Contact us
                </ContactLink>
              </p>
            </div>
          </section>

          {/* Footer links */}
          <footer className="pt-8 mt-10 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <Link
                to="/privacy"
                className="hover:text-primary-600 underline"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                to="/login"
                className="hover:text-primary-600 underline"
              >
                Sign In
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                to="/register"
                className="hover:text-primary-600 underline"
              >
                Create Account
              </Link>
            </div>
          </footer>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-500 text-sm">
            Copyright {new Date().getFullYear()} FlowIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
